import _ from 'lodash'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

import { Button, Container, Stack } from '@mui/material'

import { useGetNetworkQuery } from '@/core/services/network'
import { useListWaitingRoomsQuery } from '@/core/services/waiting-room'

import NotFoundPage from '@/pages/error/NotFoundPage'
import WaitingRoomsTable, { WaitingRoomData, WaitingRoomsTableColumn } from '@/pages/waiting-rooms/components/WaitingRoomsTable'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { isStaff } from '@/utils/type-guards'
import { useNetworkMemberPermissions } from '../hooks/useNetworkMemberPermissions'

const invisibleColumns: WaitingRoomsTableColumn[] = ['network', 'isFavorite', 'price']

export default function NetworkWaitingRooms() {
  const { t } = useTranslation()

  const { networkId } = useParams<{ networkId: string }>()

  const authUser = useAppSelector(selectAuthUser)

  const { data: network } = useGetNetworkQuery(networkId ?? skipToken)
  const { isNetworkAdmin, isNetworkMember } = useNetworkMemberPermissions({
    networkId,
    networkCreatorId: network?.createdBy._id,
    members: network?.members
  })

  const { waitingRooms } = useListWaitingRoomsQuery(
    { query: { network: networkId }, options: { limit: 0 } },
    {
      skip: !authUser || !isNetworkMember,
      selectFromResult: ({ data }) => ({
        waitingRooms: _.compact(
          _.map(data?.items, (waitingRoom) => {
            if (!authUser || !isNetworkMember) return null

            const openingHour = _.find(waitingRoom.openingHours, ({ day }) => day === dayjs().day())
            let isOpening = false
            if (openingHour) {
              const { periods } = openingHour
              isOpening = _.some(periods, (period) => dayjs().isBetween(dayjs(period.open, 'HH:mm'), dayjs(period.close, 'HH:mm')))
            }

            const hasJoinedWaitingRoom = waitingRoom.doctors.includes(authUser._id)
            const allowedActions: WaitingRoomData['allowedActions'] = []
            if (isStaff(authUser) && authUser.status === 'MEDECIN' && isNetworkMember && !hasJoinedWaitingRoom) allowedActions.push('join')
            if (hasJoinedWaitingRoom) allowedActions.push('access', 'leave')
            if (waitingRoom.createdBy === authUser?._id) allowedActions.push('delete', 'edit')
            if (isStaff(authUser) && authUser?.status === 'PDS' && isOpening && waitingRoom.doctors.length > 0) allowedActions.push('reserve')
            if (isNetworkAdmin) allowedActions.push('view-consultations')

            const isFavorite = isStaff(authUser) && (authUser.favoriteWaitingRooms ?? []).includes(waitingRoom._id)

            return {
              id: waitingRoom._id,
              isFavorite,
              isOpening,
              name: waitingRoom.name,
              specialities: waitingRoom.specialities.map((speciality) => speciality.name),
              price: waitingRoom.price !== 0 ? waitingRoom.price : undefined,
              allowedActions,
              network: network,
            }
          })
        ),
      }),
    }
  )

  if (!isNetworkMember) {
    return (
      <Stack alignItems="center" justifyContent="center" width="100%" height="100%">
        <NotFoundPage />
      </Stack>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="end" mb="30px">
        {isNetworkAdmin && (
          <Button component={Link} to={`/networks/${networkId}/waiting-rooms/create`} variant="contained">
            {t('page.waitingRooms.createWaitingRoomButton.label')}
          </Button>
        )}
      </Stack>
      <WaitingRoomsTable data={waitingRooms} invisibleColumns={invisibleColumns} />
    </Container>
  )
}
